import { onUnmounted } from "vue";
import { logEmbeddedCheckoutProblem } from "@/helpers/logEmbeddedCheckoutProblem";

/**
 * Minimum required height at each width breakpoint, sorted narrowest to widest.
 * At a given viewport width, the required height is the height for the
 * narrowest breakpoint that is >= the current width.
 */
const MIN_DIMENSIONS = [
    { width: 360, height: 1040 },
    { width: 480, height: 930 },
    { width: 840, height: 850 },
];

/** Debounce delay for resize checks, in ms */
const RESIZE_DEBOUNCE_MS = 300;

/**
 * Finds the minimum required height for the given width, using
 * MIN_DIMENSIONS as a step function. Widths narrower than the
 * smallest breakpoint use that breakpoint's height as a floor;
 * widths wider than the largest breakpoint use its height too.
 *
 * @param width - Current iframe viewport width in CSS pixels.
 * @returns Minimum viewport height in CSS pixels for the matching breakpoint.
 */
const getMinHeightForWidth = (width: number): number => {
    const sorted = [...MIN_DIMENSIONS].sort((a, b) => a.width - b.width);

    const match = sorted.find((entry) => width <= entry.width);
    if (match) {
        return match.height;
    }

    return sorted[sorted.length - 1].height;
};

/**
 * Returns the narrowest supported iframe viewport width.
 *
 * @returns Minimum viewport width in CSS pixels from the dimension conventions.
 */
const getMinWidth = (): number => {
    const sorted = [...MIN_DIMENSIONS].sort((a, b) => a.width - b.width);
    return sorted[0].width;
};

/**
 * Watches viewport size and reports undersized iframes through the shared logger.
 * Warnings include actual and required dimensions, a docs link, a Sentry
 * breadcrumb and a parent INTEGRATION_DIAGNOSTIC message. The logger suppresses
 * hosted checkout and repeats per page load.
 * Call `startEmbedSizeMonitor()` from onMounted in the consuming component.
 * Must be created during component setup so unmount cleanup is registered.
 *
 * @param report - Non-throwing diagnostic reporter; defaults to the shared logger.
 * @returns Controls to start monitoring or stop it before automatic unmount cleanup.
 */
export const useEmbedSizeMonitor = (
    report: typeof logEmbeddedCheckoutProblem = logEmbeddedCheckoutProblem,
) => {
    let resizeDebounceTimer: ReturnType<typeof setTimeout> | undefined;

    /**
     * Compares the current viewport with the documented width and height limits.
     * Reports a warning with actual and required dimensions if either is too small.
     * Page-load deduplication and embedded-only output belong to the reporter.
     */
    const checkEmbedDimensions = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const requiredHeight = getMinHeightForWidth(width);
        const requiredWidth = getMinWidth();

        if (width < requiredWidth || height < requiredHeight) {
            report({
                checkId: "iframe-size",
                level: "warning",
                message: `The iframe viewport is ${width}px wide by ${height}px high. Use a minimum width of ${requiredWidth}px and a minimum height of ${requiredHeight}px at this width to avoid cropped content or extra scrolling.`,
                docsPage: "iframe-setup",
            });
        }
    };

    /**
     * Replaces any pending dimension check with one scheduled 300ms after this
     * resize, avoiding repeated checks while the iframe container is changing.
     */
    const handleWindowResize = () => {
        clearTimeout(resizeDebounceTimer);
        resizeDebounceTimer = setTimeout(
            checkEmbedDimensions,
            RESIZE_DEBOUNCE_MS,
        );
    };

    /**
     * Checks dimensions immediately and subscribes to debounced window resizes.
     * Repeated calls recheck dimensions without adding duplicate resize listeners.
     */
    const startEmbedSizeMonitor = () => {
        checkEmbedDimensions();
        window.addEventListener("resize", handleWindowResize);
    };

    /**
     * Removes the resize listener and cancels any pending dimension check.
     * Safe to call repeatedly; also runs automatically when the component unmounts.
     */
    const stopEmbedSizeMonitor = () => {
        window.removeEventListener("resize", handleWindowResize);
        clearTimeout(resizeDebounceTimer);
    };

    onUnmounted(stopEmbedSizeMonitor);

    return { startEmbedSizeMonitor, stopEmbedSizeMonitor };
};
